import {AnyAction} from "@buildwithflux/core";
import {SubscribedTypeCallback, SubscriptionManager} from "@buildwithflux/repositories";
import {guid} from "@buildwithflux/shared";
import {z} from "zod";

interface ActionBakeObserverEventPayload {
    success: boolean;
}

interface AsyncDispatchOptions {
    awaitDrcCompletion?: boolean;
}

const actionWithSubscriptionKeyInPayload = z.object({
    payload: z.object({
        subscriptionKey: z.string(),
    }),
});

export class ActionBakeObserver {
    private readonly subscriptionManager = new SubscriptionManager<string, ActionBakeObserverEventPayload>();

    /**
     * Subscribe to changes in action baking.
     */
    public subscribe(subscriptionKey: string, onBake: SubscribedTypeCallback<ActionBakeObserverEventPayload>) {
        return this.subscriptionManager.addSubscription(subscriptionKey, onBake);
    }

    /**
     * Notify listeners that the action has been baked
     * @param subscriptionKey
     */
    public notify(subscriptionKey: string, success: boolean): void;
    public notify(action: AnyAction, success: boolean): void;
    public notify(subscriptionKeyOrAction: string | AnyAction, success: boolean) {
        if (typeof subscriptionKeyOrAction === "string") {
            const subscriptionKey = subscriptionKeyOrAction;
            this.subscriptionManager.notify(subscriptionKey, {success});
            return;
        }

        const action = subscriptionKeyOrAction;
        const subscriptionKey = this.extractSubscriptionKeyFromAction(action);
        if (subscriptionKey) {
            this.subscriptionManager.notify(subscriptionKey, {success});
        }
    }

    /**
     * Notify listeners that DRC completion has occurred for an action.
     * @param subscriptionKeyOrAction - Either a subscription key string or an action containing a subscription key
     */
    public notifyDrcCompletion(action: AnyAction): void;
    public notifyDrcCompletion(subscriptionKey: string): void;
    public notifyDrcCompletion(subscriptionKeyOrAction: string | AnyAction) {
        if (typeof subscriptionKeyOrAction === "string") {
            const subscriptionKey = subscriptionKeyOrAction;
            this.subscriptionManager.notify(this.createDrcSubcriptionKey(subscriptionKey), {success: true});
            return;
        }

        const action = subscriptionKeyOrAction;
        const subscriptionKey = this.extractSubscriptionKeyFromAction(action);
        if (subscriptionKey) {
            this.subscriptionManager.notify(this.createDrcSubcriptionKey(subscriptionKey), {success: true});
        }
    }

    /**
     * Execute a store method and wait for it to be baked.
     * @param executeWithSubscriptionKey - a callback that calls a direct store method with the subscription key
     * @returns A promise that resolves when the action is baked
     */
    public asyncDispatch(
        executeWithSubscriptionKey: (subscriptionKey: string) => void,
        options?: AsyncDispatchOptions,
    ) {
        const subscriptionKey = guid();
        return new Promise<ActionBakeObserverEventPayload>((resolve) => {
            const key = options?.awaitDrcCompletion ? this.createDrcSubcriptionKey(subscriptionKey) : subscriptionKey;
            const unsubscribe = this.subscribe(key, (payload) => {
                unsubscribe();
                resolve(payload);
            });
            executeWithSubscriptionKey(subscriptionKey);
        });
    }

    private extractSubscriptionKeyFromAction(action: AnyAction): string | undefined {
        const parsed = actionWithSubscriptionKeyInPayload.safeParse(action);
        if (parsed.success) {
            return parsed.data.payload.subscriptionKey;
        }
        return undefined;
    }

    private createDrcSubcriptionKey(baseKey: string): string {
        return `${baseKey}::drc`;
    }
}
